import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Sparkles, Check, Loader2, ArrowRight, MessageCircle, Film, BookOpen, Target, AlertTriangle } from "lucide-react";
import { STEPS } from "../../data/discoveryQuestions";
import { computeFit } from "../../lib/fitSummary";
import { findVideo } from "../../data/videoLibrary";
import { findBySlug } from "../../data/learningArticles";
import { api } from "../../lib/api";
import { useDocMeta } from "../../lib/useDocMeta";
import { track, EVENT } from "../../lib/publicAnalytics";
import { toast } from "sonner";

/**
 * AI Discovery — public, rule-based.
 * NO LLM, NO scoring endpoint, NO CRM access. Pure heuristic.
 */
export default function Discovery() {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [lead, setLead] = useState({ name: "", company: "", email: "", phone: "", website: "", message: "" });
  const [leadSent, setLeadSent] = useState(false);
  const [emailQueued, setEmailQueued] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useDocMeta({
    title: "AI Discovery · GovBidIQ",
    description: "A 90-second guided AI Discovery — answer a few questions and get a personal Fit Summary with recommended features, videos, and next steps.",
    path: "/discovery",
  });

  useEffect(() => { track(EVENT.AI_DISCOVERY_STARTED, {}); }, []);

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  const stepValid = useMemo(() => {
    return step.fields.every((f) => {
      if (!f.required) return true;
      const v = answers[f.id];
      if (f.kind === "single") return !!v;
      if (f.kind === "multi") return Array.isArray(v) && v.length > 0;
      if (f.kind === "text") return !!(v && v.trim());
      return true;
    });
  }, [step, answers]);

  const fit = done ? computeFit(answers) : null;

  const advance = () => {
    track(EVENT.AI_DISCOVERY_STEP_COMPLETED, { step: step.key, idx: stepIdx + 1 });
    if (isLast) {
      track(EVENT.AI_DISCOVERY_COMPLETED, {});
      track(EVENT.FIT_SUMMARY_GENERATED, {});
      setDone(true);
    } else {
      setStepIdx(stepIdx + 1);
    }
  };

  const back = () => stepIdx > 0 && setStepIdx(stepIdx - 1);

  const setAns = (id, val) => setAnswers({ ...answers, [id]: val });
  const toggle = (id, optId, max) => {
    const cur = Array.isArray(answers[id]) ? answers[id] : [];
    const next = cur.includes(optId) ? cur.filter((x) => x !== optId) : (max && cur.length >= max ? cur : [...cur, optId]);
    setAns(id, next);
  };

  const submitLead = async (e) => {
    e?.preventDefault();
    if (!lead.email.trim() || !lead.name.trim()) { toast.error("Name and email are required."); return; }
    setBusy(true);
    try {
      const res = await api.post("/leads", {
        email: lead.email.trim().toLowerCase(),
        name: lead.name.trim(),
        company_name: lead.company.trim(),
        source: "discovery",
        metadata: {
          phone: lead.phone, website: lead.website, message: lead.message,
          discovery_answers: answers,
          fit_strength: fit?.fit_strength,
          fit: fit || null,
        },
      });
      const queued = !!res?.data?.email_queued;
      track(EVENT.LEAD_FORM_SUBMITTED, { source: "discovery", fit_strength: fit?.fit_strength, email_queued: queued });
      setEmailQueued(queued);
      setLeadSent(true);
      toast.success(queued
        ? "Thanks — your Fit Summary has been queued for email delivery."
        : "Thanks — we received your info. Please contact BidNoBidIQ@aol.com if the summary does not arrive.");
    } catch {
      toast.error("Could not submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done && fit) return renderFitSummary({ fit, answers, lead, setLead, submitLead, leadSent, emailQueued, busy, nav, restart: () => { setStepIdx(0); setDone(false); } });

  return (
    <div className="max-w-[900px] mx-auto px-6 py-12">
      <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ AI Discovery</div>
      <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-100 leading-tight mb-2">Let's find your fit in 90 seconds.</h1>
      <p className="text-slate-400 text-base max-w-2xl leading-relaxed mb-6">
        Four short questions. No signup. You'll get a personal Fit Summary, recommended videos, and a next step — instantly.
      </p>

      <div className="flex items-center gap-1.5 mb-6" role="progressbar" aria-valuenow={stepIdx + 1} aria-valuemin={1} aria-valuemax={STEPS.length}>
        {STEPS.map((_, i) => (
          <div key={i} className={`h-1 flex-1 ${i <= stepIdx ? "bg-sky-500" : "bg-slate-800"} transition-colors`} />
        ))}
      </div>

      <div className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-6 sm:p-8 mb-5 min-h-[300px]" data-testid={`discovery-step-${stepIdx + 1}`}>
        <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-3">Step {stepIdx + 1} of {STEPS.length}</div>
        <h2 className="font-display font-bold text-2xl text-slate-100 mb-5">{step.title}</h2>
        <div className="space-y-5">
          {step.fields.map((f) => (
            <div key={f.id}>
              <label className="block font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-2">{f.label}{f.required && <span className="text-sky-400 ml-1">*</span>}</label>
              {f.kind === "single" && (
                <div className="flex flex-wrap gap-2" role="radiogroup">
                  {f.options.map((o) => {
                    const active = answers[f.id] === o.id;
                    return (
                      <button key={o.id} type="button" role="radio" aria-checked={active} onClick={() => setAns(f.id, o.id)} data-testid={`disc-${f.id}-${o.id}`} className={`font-mono text-[11px] tracking-widest uppercase px-3.5 py-2 border transition-colors ${active ? "bg-sky-500 text-slate-950 border-sky-500" : "border-slate-700 text-slate-300 hover:border-sky-500 hover:text-sky-300"}`}>
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              )}
              {f.kind === "multi" && (
                <div className="flex flex-wrap gap-2" role="group">
                  {f.options.map((o) => {
                    const cur = Array.isArray(answers[f.id]) ? answers[f.id] : [];
                    const active = cur.includes(o.id);
                    return (
                      <button key={o.id} type="button" aria-pressed={active} onClick={() => toggle(f.id, o.id, f.max)} data-testid={`disc-${f.id}-${o.id}`} className={`font-mono text-[11px] tracking-widest uppercase px-3.5 py-2 border transition-colors inline-flex items-center gap-1.5 ${active ? "bg-sky-500 text-slate-950 border-sky-500" : "border-slate-700 text-slate-300 hover:border-sky-500 hover:text-sky-300"}`}>
                        {active && <Check className="w-3 h-3" strokeWidth={3} />}
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              )}
              {f.kind === "text" && (
                <input type="text" value={answers[f.id] || ""} onChange={(e) => setAns(f.id, e.target.value)} placeholder={f.placeholder} data-testid={`disc-${f.id}`} className="w-full bg-slate-950 border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm px-3 py-2.5 font-mono" />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={back} disabled={stepIdx === 0} data-testid="discovery-back" className="inline-flex items-center gap-2 border border-slate-700 hover:border-slate-500 text-slate-300 font-mono text-[10px] tracking-widest uppercase px-4 py-2.5 disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">{stepIdx + 1} / {STEPS.length}</div>
        <button onClick={advance} disabled={!stepValid} data-testid={isLast ? "discovery-finish" : "discovery-next"} className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
          {isLast ? <>Generate Fit Summary <Sparkles className="w-3.5 h-3.5" /></> : <>Next <ChevronRight className="w-3.5 h-3.5" /></>}
        </button>
      </div>
    </div>
  );
}

function renderFitSummary({ fit, lead, setLead, submitLead, leadSent, emailQueued, busy, restart }) {
  const videos = fit.recommended_videos.map((s) => findVideo(s)).filter(Boolean);
  const lessons = fit.recommended_learning.map((s) => findBySlug(s)).filter(Boolean);

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-12">
      <div className="border border-emerald-500/40 bg-emerald-500/5 p-3 mb-6 flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" strokeWidth={2.5} />
        <div className="text-sm">
          <div className="font-mono text-[10px] tracking-widest uppercase text-emerald-300 mb-1">/ Fit Summary Ready</div>
          <p className="text-slate-300 leading-relaxed">Heuristic recommendation. Not a production scoring output — see the Sample Report for the real format.</p>
        </div>
      </div>

      <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-2">/ Your GovBidIQ Fit Summary</div>
      <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-100 leading-tight mb-2">{fit.visitor_label} · {fit.fit_strength} fit</h1>
      <p className="text-slate-400 mb-8">{fit.engagement_note}</p>

      <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)] mb-6">
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-2 inline-flex items-center gap-1.5"><Target className="w-3 h-3" />/ Visitor Profile</div>
          <ul className="space-y-1 text-sm text-slate-200">
            <li><span className="text-slate-500">Type:</span> {fit.visitor_label}</li>
            {fit.set_asides.length > 0 && <li><span className="text-slate-500">Set-asides:</span> {fit.set_asides.map((s) => s.toUpperCase()).join(", ")}</li>}
            <li><span className="text-slate-500">Top challenges:</span> {fit.challenges.length} selected</li>
          </ul>
        </div>
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="font-mono text-[10px] tracking-widest uppercase text-rose-400 mb-2 inline-flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" />/ Main Challenges</div>
          <ul className="space-y-1 text-sm text-slate-200">
            {fit.challenges.length === 0 && <li className="text-slate-500 italic">None selected.</li>}
            {fit.challenges.map((c) => <li key={c}>· {c.replace(/_/g, " ")}</li>)}
          </ul>
        </div>
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="font-mono text-[10px] tracking-widest uppercase text-emerald-400 mb-2 inline-flex items-center gap-1.5"><Check className="w-3 h-3" />/ Best-Fit Features</div>
          <ul className="space-y-1 text-sm text-slate-200">
            {fit.features.map((f) => <li key={f}>· {f}</li>)}
          </ul>
        </div>
      </div>

      {fit.use_case && (
        <Link to="/use-cases" className="block border border-sky-500/30 bg-sky-500/5 p-5 mb-6 hover:bg-sky-500/10 transition-colors" data-testid="fit-usecase-link">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-1">/ Recommended Use Case</div>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-display font-bold text-slate-100 text-lg">{fit.use_case.label} → {fit.use_case.persona}</div>
              <div className="text-sm text-slate-400 mt-1">{fit.use_case.next_step}</div>
            </div>
            <ArrowRight className="w-5 h-5 text-sky-400 shrink-0" />
          </div>
        </Link>
      )}

      {videos.length > 0 && (
        <div className="mb-6">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3 inline-flex items-center gap-1.5"><Film className="w-3 h-3" />/ Suggested Videos</div>
          <div className="grid sm:grid-cols-3 gap-px bg-[var(--border-soft)]">
            {videos.map((v) => (
              <Link key={v.slug} to="/demo-library" className="bg-[var(--bg-secondary)] p-4 hover:bg-slate-900/70" data-testid={`fit-video-${v.slug}`}>
                <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-1">{v.watch_minutes} min · {v.category}</div>
                <div className="font-display font-bold text-slate-100 text-sm">{v.title}</div>
                <div className="text-xs text-slate-400 mt-1">{v.description}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {lessons.length > 0 && (
        <div className="mb-8">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3 inline-flex items-center gap-1.5"><BookOpen className="w-3 h-3" />/ Suggested Reading</div>
          <div className="grid sm:grid-cols-2 gap-px bg-[var(--border-soft)]">
            {lessons.map((l) => (
              <Link key={l.slug} to={`/learning/${l.slug}`} className="bg-[var(--bg-secondary)] p-3 hover:bg-slate-900/70" data-testid={`fit-lesson-${l.slug}`}>
                <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-0.5">{l.category}</div>
                <div className="font-display font-bold text-slate-100 text-sm">{l.title}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-px bg-[var(--border-soft)] mb-8">
        <div className="bg-[var(--bg-secondary)] p-5">
          <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-2">/ Suggested Next Step</div>
          <Link to={fit.next_step.to} data-testid="fit-next-step" className="block">
            <div className="font-display font-bold text-slate-100 text-xl mb-1 hover:text-sky-300">{fit.next_step.label}</div>
            <div className="text-sm text-slate-400 inline-flex items-center gap-1">Go now <ArrowRight className="w-3.5 h-3.5" /></div>
          </Link>
        </div>
        <div className="bg-[var(--bg-secondary)] p-5 grid grid-cols-2 gap-2">
          <Link to="/tour" data-testid="fit-cta-tour" className="border border-slate-700 hover:border-sky-500 text-slate-300 hover:text-sky-300 font-mono text-[10px] tracking-widest uppercase px-3 py-2.5 text-center">Self-Demo (Tour)</Link>
          <Link to="/sample" data-testid="fit-cta-sample" className="border border-slate-700 hover:border-sky-500 text-slate-300 hover:text-sky-300 font-mono text-[10px] tracking-widest uppercase px-3 py-2.5 text-center">Sample Report</Link>
          <Link to="/strategy-call" data-testid="fit-cta-strategy" className="border border-slate-700 hover:border-sky-500 text-slate-300 hover:text-sky-300 font-mono text-[10px] tracking-widest uppercase px-3 py-2.5 text-center">Strategy Call</Link>
          <Link to="/demo-library" data-testid="fit-cta-demo" className="border border-slate-700 hover:border-sky-500 text-slate-300 hover:text-sky-300 font-mono text-[10px] tracking-widest uppercase px-3 py-2.5 text-center">Demo Library</Link>
        </div>
      </div>

      {/* Optional lead capture */}
      <div className="border border-sky-500/30 bg-sky-500/5 p-5">
        {leadSent ? (
          <div className="text-center py-6" data-testid="fit-lead-success">
            <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" strokeWidth={2.5} />
            {emailQueued ? (
              <>
                <div className="font-display font-bold text-slate-100 text-lg mb-1" data-testid="fit-lead-success-title">Thanks — your Fit Summary has been queued for email delivery.</div>
                <div className="text-sm text-slate-400" data-testid="fit-lead-success-sub">Check your inbox in a few minutes. Look in Spam or Junk if it does not appear soon.</div>
              </>
            ) : (
              <>
                <div className="font-display font-bold text-slate-100 text-lg mb-1" data-testid="fit-lead-success-title">Thanks — we received your information.</div>
                <div className="text-sm text-slate-400" data-testid="fit-lead-success-sub">We could not queue the email automatically. Please contact <a href="mailto:BidNoBidIQ@aol.com" className="text-sky-400 hover:text-sky-300 underline">BidNoBidIQ@aol.com</a> if you would like the summary sent to you.</div>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={submitLead} className="space-y-3">
            <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-1 inline-flex items-center gap-1.5"><MessageCircle className="w-3 h-3" /> / Optional: get this summary by email</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field testid="fit-lead-name"    label="Name *"     v={lead.name}    onChange={(v) => setLead({ ...lead, name: v })} />
              <Field testid="fit-lead-email"   label="Email *"    v={lead.email}   onChange={(v) => setLead({ ...lead, email: v })} type="email" />
              <Field testid="fit-lead-company" label="Company"    v={lead.company} onChange={(v) => setLead({ ...lead, company: v })} />
              <Field testid="fit-lead-phone"   label="Phone"      v={lead.phone}   onChange={(v) => setLead({ ...lead, phone: v })} />
              <Field testid="fit-lead-website" label="Website"    v={lead.website} onChange={(v) => setLead({ ...lead, website: v })} className="sm:col-span-2" />
            </div>
            <label className="block">
              <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-1">Anything to add?</div>
              <textarea value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} rows={2} data-testid="fit-lead-message" className="w-full bg-slate-950 border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm px-3 py-2 font-mono" />
            </label>
            <button type="submit" disabled={busy} data-testid="fit-lead-submit" className="bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-5 py-2.5 inline-flex items-center gap-2">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Email me my Fit Summary
            </button>
          </form>
        )}
      </div>

      <div className="mt-6 text-center">
        <button onClick={restart} data-testid="fit-restart" className="font-mono text-[10px] tracking-widest uppercase text-slate-500 hover:text-sky-400">
          ← Start a new discovery
        </button>
      </div>
    </div>
  );
}

function Field({ label, v, onChange, testid, type = "text", className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-1">{label}</div>
      <input type={type} value={v} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-slate-950 border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm px-3 py-2 font-mono" />
    </label>
  );
}
